import {
  GPIO_EVENTS,
  GPIO_PROPERTIES,
  GPIO_TARGET_PROPERTIES,
  GPIO_VALUE_TARGET_TYPES,
  validateGpioBindings,
} from './GpioBindingModel.js'

const clone = value => {
  if (Array.isArray(value)) return value.map(clone)
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]))
  return value
}

export const GPIO_EDITOR_PROPERTIES = Object.freeze(['state', 'brightness'])
// A state binding may also read a sensor value.
const GPIO_STATE_EDITOR_PROPERTIES = Object.freeze([...GPIO_EDITOR_PROPERTIES, 'value'])
// Properties a GPIO Entity action sets (as a binding or an entity command).
const GPIO_ACTION_EDITOR_PROPERTIES = Object.freeze(['state', 'brightness', 'rgb_color'])

export function objectTypeForBinding(objectType) {
  return objectType === 'button' ? 'btn' : objectType
}

export function eventOptionsForObject(objectType) {
  const type = objectTypeForBinding(objectType)
  if (['slider', 'arc', 'switch', 'btnmatrix'].includes(type)) return ['changed']
  if (type === 'btn') return ['click', 'down', 'up']
  return []
}

export function eventPropertyOptions(objectType, event) {
  const type = objectTypeForBinding(objectType)
  if (['slider', 'arc'].includes(type) && event === 'changed') return ['brightness']
  if (type === 'switch' && event === 'changed') return ['state']
  if (type === 'btn' && ['click', 'down', 'up'].includes(event)) return ['state']
  if (type === 'btnmatrix' && event === 'changed') return ['state', 'brightness']
  return []
}

export function statePropertyOptions(objectType) {
  const type = objectTypeForBinding(objectType)
  if (['slider', 'arc'].includes(type)) return ['brightness', 'state', 'value']
  if (GPIO_VALUE_TARGET_TYPES.includes(type)) return ['value']
  if (type === 'label') return ['state', 'value']
  if (['switch', 'btn'].includes(type)) return ['state']
  return []
}

export function stateTargetOptions(objectType, objectConfig = {}, property = '') {
  const type = objectTypeForBinding(objectType)
  if (GPIO_VALUE_TARGET_TYPES.includes(type)) return ['val']
  if (type === 'switch') return ['val', 'state']
  if (type === 'btn' && objectConfig.toggle === true) return ['val', 'state']
  if (type === 'label' && ['state', 'value'].includes(property)) return ['text']
  return []
}

export function propertyMetadata(entity, property) {
  return entity?.properties?.[property] ?? null
}

/**
 * Entity properties to offer: `mode` 'state' (a state binding reads them),
 * 'event' (an event binding writes them) or 'action' (a GPIO Entity action
 * writes them, on any event).
 */
export function availableEntityProperties(entity, { mode = 'state', objectType, event, bindingProperty = '' } = {}) {
  const candidates = mode === 'action' ? GPIO_ACTION_EDITOR_PROPERTIES
    : mode === 'event' ? eventPropertyOptions(objectType, event)
      : statePropertyOptions(objectType)
  const editable = mode === 'action' ? GPIO_ACTION_EDITOR_PROPERTIES
    : mode === 'state' ? GPIO_STATE_EDITOR_PROPERTIES : GPIO_EDITOR_PROPERTIES
  const properties = entity?.properties ?? {}
  const known = Object.keys(properties)
    .filter(property => editable.includes(property))
    .filter(property => candidates.includes(property))
    .filter(property => mode === 'state' || properties[property]?.writable !== false)
  // Offline/manual entities have no descriptor. Keep the firmware-matrix
  // candidates available so a valid hand-authored binding remains editable.
  if (!entity) return candidates
  const options = known.length ? known : candidates.filter(property => !Object.hasOwn(properties, property))
  if (bindingProperty && !options.includes(bindingProperty) && candidates.includes(bindingProperty)) {
    return [bindingProperty, ...options]
  }
  return options
}

export function bindingEntityLabel(entity, entityId) {
  if (entity) return `${entity.name || entity.id} · ${entity.domain || 'GPIO'}`
  return entityId ? `${entityId} · unavailable` : 'Select GPIO entity'
}

export function rangeForObject(node) {
  const min = Number.isInteger(Number(node?.min)) ? Number(node.min) : 0
  const max = Number.isInteger(Number(node?.max)) ? Number(node.max) : 100
  return min === max ? { min, max: max + 1 } : { min, max }
}

export function rangeForProperty(entity, property) {
  const metadata = propertyMetadata(entity, property)
  // Bindings map integers; a sensor's scaled range may be fractional.
  const min = Number.isFinite(metadata?.min) ? Math.round(metadata.min) : 0
  const max = Number.isFinite(metadata?.max) ? Math.round(metadata.max) : 255
  return min === max ? { min, max: max + 1 } : { min, max }
}

export function createEventBinding(node, entity = null) {
  const event = eventOptionsForObject(node?.obj)[0] ?? GPIO_EVENTS[0]
  const property = eventPropertyOptions(node?.obj, event)[0] ?? GPIO_PROPERTIES[0]
  const objectRange = rangeForObject(node)
  const propertyRange = rangeForProperty(entity, property)
  return {
    event,
    source_property: property === 'state' ? 'state' : 'val',
    entity: entity?.id ?? '',
    property,
    ...(property === 'brightness' ? {
      in_min: objectRange.min,
      in_max: objectRange.max,
      out_min: propertyRange.min,
      out_max: propertyRange.max,
    } : {}),
  }
}

export function createStateBinding(node, entity = null) {
  // The object's first property the entity has (a sensor only has `value`).
  const available = availableEntityProperties(entity, { mode: 'state', objectType: node?.obj })
  const property = statePropertyOptions(node?.obj).find(item => available.includes(item))
    ?? statePropertyOptions(node?.obj)[0] ?? 'state'
  const target_property = stateTargetOptions(node?.obj, node, property)[0] ?? 'val'
  return {
    entity: entity?.id ?? '',
    property,
    target_property,
    ...stateBindingRanges(node, entity, property, target_property),
  }
}

/** Explicit default mapping of a new state binding, or {} when it maps nothing. */
export function stateBindingRanges(node, entity, property, target_property) {
  if (!mappingVisible({ property, target_property }, 'state', node?.obj)) return {}
  // A state is 0/1, like the firmware's default input range for it.
  const propertyRange = property === 'state' ? { min: 0, max: 1 } : rangeForProperty(entity, property)
  const objectRange = rangeForObject(node)
  return { in_min: propertyRange.min, in_max: propertyRange.max, out_min: objectRange.min, out_max: objectRange.max }
}

export function mappingVisible(binding, mode, objectType) {
  if (mode === 'event') return binding.property === 'brightness'
  return binding.property === 'brightness' || (['state', 'value'].includes(binding.property) &&
    binding.target_property === 'val' && GPIO_VALUE_TARGET_TYPES.includes(objectTypeForBinding(objectType)))
}

export function mappingFallback(binding, key, mode, node, entity) {
  if (binding?.[key] !== undefined) return binding[key]
  const objectRange = rangeForObject(node)
  const propertyRange = rangeForProperty(entity, binding?.property)
  if (mode === 'event') return key.startsWith('in_') ? objectRange[key.endsWith('min') ? 'min' : 'max'] : propertyRange[key.endsWith('min') ? 'min' : 'max']
  return key.startsWith('in_') ? propertyRange[key.endsWith('min') ? 'min' : 'max'] : objectRange[key.endsWith('min') ? 'min' : 'max']
}

export function bindingDiagnostics(binding, mode, node, metadata) {
  const value = mode === 'event' ? { events: [binding], state: [] } : { events: [], state: [binding] }
  return validateGpioBindings(value, {
    objectType: node?.obj,
    objectConfig: node,
    metadata,
  }).diagnostics.map(item => ({
    ...item,
    bindingIndex: item.binding ?? 0,
  }))
}

export function bindingSummary(binding, mode, entity) {
  if (mode === 'event') {
    return `${binding.event || 'event'} · ${binding.source_property || 'val'} → ${binding.entity || 'entity'}.${binding.property || 'property'}`
  }
  return `${binding.entity || 'entity'}.${binding.property || 'property'} → ${binding.target_property || 'target'}`
}

export function cloneBinding(binding) {
  return clone(binding)
}

export const GPIO_TARGET_PROPERTY_OPTIONS = GPIO_TARGET_PROPERTIES
